import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Download, Plus } from "lucide-react";

export default function ChefDocuments() {
  const [items, setItems] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [open, setOpen] = useState(false);
  const [titre, setTitre] = useState("");
  const [salarieId, setSalarieId] = useState("all");
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const [d, s] = await Promise.all([
        api.get("/chef/documents"),
        api.get("/chef/salaries"),
      ]);
      setItems(d.data);
      setSalaries(s.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file || !titre) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams({ titre });
      if (salarieId && salarieId !== "all") params.append("salarie_id", salarieId);
      await api.post(`/chef/documents?${params.toString()}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document déposé");
      setOpen(false);
      setTitre("");
      setSalarieId("all");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const download = async (id, name) => {
    try {
      const res = await api.get(`/files/document/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Téléchargement impossible"); }
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="RH"
        title="Documents RH"
        sub="Déposez les contrats, attestations, règlements intérieurs. Partagez en commun ou par salarié."
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="chef-upload-doc-btn" className="h-11 bg-[#002FA7] hover:bg-[#002175] text-white font-semibold">
                <Plus className="h-4 w-4 mr-2" /> Ajouter un document
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">Nouveau document</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="chef-doc-form">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Titre</Label>
                  <Input value={titre} onChange={(e) => setTitre(e.target.value)} data-testid="doc-titre" className="h-11" required />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Destinataire</Label>
                  <Select value={salarieId} onValueChange={setSalarieId}>
                    <SelectTrigger data-testid="doc-salarie" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les salariés</SelectItem>
                      {salaries.map((s) => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Fichier</Label>
                  <input ref={fileRef} type="file" required data-testid="doc-file" className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#0A0A0A] file:text-white file:font-semibold" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={uploading} data-testid="doc-submit" className="bg-[#002FA7] hover:bg-[#002175] text-white">
                    <Upload className="h-4 w-4 mr-2" /> {uploading ? "Envoi…" : "Déposer"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-6 lg:p-10">
        <div className="bg-white border border-[#DEE2E6]" data-testid="chef-docs-list">
          <div className="grid grid-cols-12 gap-4 p-4 text-xs uppercase tracking-[0.2em] font-semibold bg-[#F8F9FA] border-b border-[#DEE2E6]">
            <div className="col-span-4">Titre</div>
            <div className="col-span-3">Destinataire</div>
            <div className="col-span-3">Fichier</div>
            <div className="col-span-1">Date</div>
            <div className="col-span-1 text-right"></div>
          </div>
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#868E96]">Aucun document.</div>
          ) : items.map((d) => {
            const s = salaries.find((x) => x.id === d.salarie_id);
            return (
              <div key={d.id} className="grid grid-cols-12 gap-4 p-4 border-b border-[#DEE2E6] items-center text-sm" data-testid={`doc-row-${d.id}`}>
                <div className="col-span-4 font-semibold">{d.titre}</div>
                <div className="col-span-3">{s ? `${s.prenom} ${s.nom}` : "Tous"}</div>
                <div className="col-span-3 truncate text-xs text-[#495057]">{d.original_filename}</div>
                <div className="col-span-1 text-xs text-[#868E96]">{new Date(d.uploaded_at).toLocaleDateString("fr-FR")}</div>
                <div className="col-span-1 text-right">
                  <Button size="sm" variant="outline" onClick={() => download(d.id, d.original_filename)} data-testid={`doc-download-${d.id}`}><Download className="h-3 w-3" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
